// src/pages/forge/SquadAuth.tsx
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Flame, AlertCircle, ArrowLeft, Users, Key, Mail, UserPlus, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useSquadAuth } from '@/contexts/SquadAuthContext';

export default function SquadAuth() {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading: authLoading } = useSquadAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Login form state
  const [loginData, setLoginData] = useState({
    squadCode: '',
    email: '',
    password: ''
  });

  // Check if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/forge/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const success = await login(loginData.squadCode, loginData.email, loginData.password);
      
      if (!success) {
        setError('Invalid squad code, email, or password. Please check your credentials.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleStartRegistration = () => {
    navigate('/forge/register');
  };

  const handleLoginTabClick = () => {
    setActiveTab('login');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If authenticated, don't render the login form
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute top-4 left-4">
        <Button variant="ghost" className="text-muted-foreground hover:text-foreground" asChild>
          <Link to="/forge">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Forge
          </Link>
        </Button>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-primary/10 rounded-full">
              <Flame className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl text-foreground">Squad Portal</CardTitle>
          <CardDescription className="text-muted-foreground">
            Access The Forge phase dashboard
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-muted">
              <TabsTrigger value="login" className="data-[state=active]:bg-background data-[state=active]:text-foreground">Login</TabsTrigger>
              <TabsTrigger value="register" className="data-[state=active]:bg-background data-[state=active]:text-foreground">Register Squad</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4 mt-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="squadCode" className="text-foreground">Squad Code</Label>
                  <div className="relative">
                    <Users className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="squadCode"
                      placeholder="e.g., FORGE-2024-01"
                      className="pl-10"
                      value={loginData.squadCode}
                      onChange={(e) => setLoginData({...loginData, squadCode: e.target.value})}
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Enter the squad code provided by your squad lead</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground">Your Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="member@squad.com"
                      className="pl-10"
                      value={loginData.email}
                      onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Use the email you registered with your squad</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-foreground">Password</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className="pl-10"
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                      disabled={loading}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">Create a password during registration or use the one provided</p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Accessing...
                    </div>
                  ) : (
                    'Access Squad Dashboard'
                  )}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <div className="space-y-6 mt-4">
                <div className="text-center p-6 bg-primary/5 rounded-lg border border-primary/20">
                  <UserPlus className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-foreground mb-2">Start Your Squad Registration</h3>
                  <p className="text-muted-foreground mb-6">
                    Join The Forge 90-day intensive program. You'll complete a step-by-step application including team formation and individual assessments.
                  </p>
                  <Button 
                    onClick={handleStartRegistration}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
                  >
                    Begin Registration
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="text-center p-3 bg-muted/30 rounded-lg">
                    <div className="font-medium text-foreground">Step 1</div>
                    <div className="text-xs text-muted-foreground">Squad Info</div>
                  </div>
                  <div className="text-center p-3 bg-muted/30 rounded-lg">
                    <div className="font-medium text-foreground">Step 2</div>
                    <div className="text-xs text-muted-foreground">Team Members</div>
                  </div>
                  <div className="text-center p-3 bg-muted/30 rounded-lg">
                    <div className="font-medium text-foreground">Step 3</div>
                    <div className="text-xs text-muted-foreground">Assessment</div>
                  </div>
                  <div className="text-center p-3 bg-muted/30 rounded-lg">
                    <div className="font-medium text-foreground">Step 4</div>
                    <div className="text-xs text-muted-foreground">Review</div>
                  </div>
                </div>

                <p className="text-xs text-center text-muted-foreground">
                  Already have a squad code? <button onClick={handleLoginTabClick} className="text-primary hover:underline bg-transparent border-none p-0 cursor-pointer">Log in here</button>
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>

        <CardFooter className="flex justify-center border-t pt-4">
          <p className="text-sm text-muted-foreground">
            All squad members use the same squad code after approval.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}